import { DefaultMessages, ProgramThemeMessages, SegmentSpecificMessages } from '@sheerid/jslib';
import { RecursivePartial } from '../types/types';


type LibraryMessages = DefaultMessages & SegmentSpecificMessages & ProgramThemeMessages;

/**
 * Feel free to modify this type for this project (only) here
 * For example, if you add a whole new block of text that the jslib does not have a spot for already
 *
 * This is not necessary if you are just overriding text for a pre-existing message id
 */
type ThisProjectMessages = {
    viewOtherHuluPlans: string,
    gotIt: string,
    studentVerification: string,
    disclaimer: string,
    errorTitle: {
        invalidProgram: string,
        noProgram: string,
        simulatedError: string,
        unauthorizedDomain: string,
        failedToFetch: string,
        internalServerError: string,
        requestTimeout: string,
        expiredProgram: string,
        apiRateLimitExceeded: string,
        expiredVerification: string,
        fraudRulesReject: string,
        verificationLimitExceeded: string,
        docReviewLimitExceeded: string,
    },
};

/**
 * Define the actual language strings here, as needed.
 * This object should be sparse, so you inherit language from the library. Only override what you need to.
 *
 * The complicated type ensures your object adheres to the library's type(s), as well as any additional type you've
 * created in ThisProjectMessages.
 */
export const messagesEN: RecursivePartial<LibraryMessages & ThisProjectMessages> = {
    schoolName: 'College Name',
    birthDate: 'Birth Date',
    emailAddress: 'Email',
    viewOtherHuluPlans: 'View Other Hulu Plans',
    gotIt: 'Got It',
    howDoesVerifyingWork: 'How does it work?',
    studentInfoShared: 'Information entered here will be used for verification purposes. Verification services powered by SheerID.',
    studentVerification: 'Student Verification',
    disclaimer: 'You are currently on SheerID\'s Website and any information entered into this form will be processed by SheerID for verification purposes and treated according to SheerID\'s <a class="sid-link" href="https://www.sheerid.com/privacy-policy/" target="_blank">privacy policy</a>. See <a class="sid-link" href="https://verify.sheerid.com/student-faq/?pid={programId}" target="_blank">SheerID FAQs</a> for more detail.',
    errorTitle: {
        invalidProgram: 'Something\'s not quite right. We will look into it.',
        noProgram: 'Something\'s not quite right. We will look into it.',
        simulatedError: 'Something\'s not quite right. We will look into it.',
        unauthorizedDomain: 'Something\'s not quite right. We will look into it.',
        failedToFetch: 'Something\'s not quite right. We will look into it.',
        internalServerError: 'Something\'s not quite right. We will look into it.',
        requestTimeout: 'Something\'s not quite right. We will look into it.',
        expiredProgram: 'This link is no longer available',
        apiRateLimitExceeded: 'We\'re currently unable to process your request.',
        expiredVerification: 'Your previous verification expired.',
        fraudRulesReject: 'You\'re not eligible for the Hulu Student Deal at this time.',
        verificationLimitExceeded: 'You\'re not eligible for the Hulu Student Deal at this time.',
        docReviewLimitExceeded: 'You\'re not eligible for the Hulu Student Deal at this time.',
    },
    errorId: {
        invalidOrganization: ' Please select an organization from the list',
        invalidFirstName: ' Please enter your first name',
        invalidLastName: ' Please enter your last name',
        invalidBirthDate: ' Please enter a valid birth date',
        invalidEmail: ' Please enter a valid email address',
        futureBirthDate: ' Please enter a valid birth date',
        underagePerson: ' Please enter a valid birth date',
        invalidProgram: 'Error Type: Invalid program',
        noProgram: 'Error Type: No program found',
        unknownError: 'Error Type: Unknown error',
        simulatedError: 'Error Type: This is a simulated error',
        unauthorizedDomain: 'Error Type: Unauthorized domain',
        failedToFetch: 'Error Type: Failed HTTP request',
        internalServerError: 'Error Type: Internal server error',
        requestTimeout: 'Error Type: Request timeout',
        apiRateLimitExceeded: 'Please try again later.',
        expiredVerification: 'Please verify again.',
        fraudRulesReject: 'The good news is we have other plans for you.',
        verificationLimitExceeded: 'The good news is we have other plans for you.',
        docReviewLimitExceeded: 'The good news is we have other plans for you.',
    },
    step: {
        personalInfo: {
            title: 'Verify Your Student Status',
            subtitle: 'We just need a few details to make sure that you\'re eligible for student status.',
        },
        docUpload: {
            title: 'Thanks - just one more thing',
            subtitle: 'We\'ll need a few minutes to review and we\'ll email you as soon as we\'re done.',
            fileInstructions: 'Drag &amp; Drop your files here or <span class="sid-dropzone-wrap--link sid-link">browse</span>',
            addFile: 'Add another file',
            submitButtonLabel: 'Submit',
            acceptableUploadExamples: 'We need you to upload one of of the following:',
            acceptableUploads: {
                student: {
                    idCard: 'School ID with expiration date clearly shown',
                    classSchedule: 'Class schedule with full name and date',
                    tuitionReceipt: 'Tuition receipt with full name and date',
                },
            },
        },
        pending: {
            titleReview: 'Thanks - our team is reviewing now',
            subtitle: 'We\'ll send you an email in a few minutes so look out for that. If you need to adjust your spam filtering, please go ahead.',
        },
        success: {
            title: 'Thanks for verifying your student status',
            subtitle: 'All you have to do is add a payment method and you can start streaming now.',
            redirectButtonText: 'Continue',
        },
    },
};
